import { CustomLoader } from "@admin/components/ui/custom-loader";
import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent } from "../../../components/ui/card";
import { DataTable, type TableColumnDef } from "../../../components/data-table";
import { Button } from "../../../components/ui/button";
import { Plus, Edit, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Switch } from "../../../components/ui/switch";
import { toast } from "sonner";
import {
  podcastsAPI,
  type Podcast,
  type PodcastCreatePayload,
  type PaginationParams,
} from "../../../services/api";
import { CommonPagination } from "../../../components/CommonPagination";

const PODCAST_FOR_OPTIONS = ["General", "Hostel", "Students", "Institution"];
const PODCAST_TYPE_OPTIONS = ["YouTube", "Audio", "Video", "Spotify"];

const initialFormState: PodcastCreatePayload = {
  name: "",
  podcast_for: PODCAST_FOR_OPTIONS[0],
  podcast_type: PODCAST_TYPE_OPTIONS[0],
  link: "",
  is_enabled: true,
};

function parseListResponse<T>(res: unknown): {
  list: T[];
  count: number;
} {
  if (!res || typeof res !== "object") {
    return { list: [], count: 0 };
  }
  const data = res as Record<string, unknown>;
  const list = (data.results ?? data.data ?? []) as T[];
  const count =
    (data.count as number) ??
    (data.total_count as number) ??
    (Array.isArray(list) ? list.length : 0);
  return { list: Array.isArray(list) ? list : [], count };
}

export default function Podcasts() {
  const [podcasts, setPodcasts] = useState<Podcast[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | number | null>(null);
  const [form, setForm] = useState<PodcastCreatePayload>(initialFormState);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const loadPodcasts = useCallback(async () => {
    setLoading(true);
    try {
      const params: PaginationParams = {
        page: currentPage,
        page_size: pageSize,
      };
      const res = await podcastsAPI.list(params);
      const { list, count } = parseListResponse<Podcast>(res);
      setPodcasts(list);
      setTotalItems(count);
      setTotalPages(Math.ceil(count / pageSize) || 0);
    } catch {
      toast.error("Failed to load podcasts");
      setPodcasts([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    loadPodcasts();
  }, [loadPodcasts]);

  const resetForm = () => {
    setForm(initialFormState);
    setEditingId(null);
  };

  const openAddModal = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditModal = (podcast: Podcast) => {
    setForm({
      name: podcast.name ?? "",
      podcast_for: podcast.podcast_for ?? PODCAST_FOR_OPTIONS[0],
      podcast_type: podcast.podcast_type ?? PODCAST_TYPE_OPTIONS[0],
      link: podcast.link ?? "",
      is_enabled: podcast.is_enabled ?? true,
    });
    setEditingId(podcast.id);
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Please enter a podcast name.");
      return;
    }
    if (!form.link.trim()) {
      toast.error("Please enter a link.");
      return;
    }
    setSaving(true);
    try {
      if (editingId !== null && editingId !== undefined) {
        await podcastsAPI.update(editingId, form);
        toast.success("Podcast updated successfully!");
      } else {
        await podcastsAPI.create(form);
        toast.success("Podcast created successfully!");
      }
      await loadPodcasts();
      setIsDialogOpen(false);
      resetForm();
    } catch {
      toast.error(
        editingId !== null && editingId !== undefined
          ? "Failed to update podcast"
          : "Failed to create podcast"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetForm();
    }
    setIsDialogOpen(open);
  };

  const handleDeleteConfirm = async () => {
    if (deleteId === null) {
      return;
    }
    try {
      await podcastsAPI.delete(deleteId);
      toast.success("Podcast deleted successfully!");
      await loadPodcasts();
      setDeleteId(null);
    } catch {
      toast.error("Failed to delete podcast");
    }
  };

  const isEditMode = editingId !== null && editingId !== undefined;

  const tableColumns = useMemo<TableColumnDef<Podcast>[]>(
    () => [
      {
        id: "sr",
        label: "Sr. No",
        width: 72,
        alwaysVisible: true,
        renderCell: (_row, rowIndex) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
      },
      {
        id: "name",
        label: "Name",
        width: 180,
        renderCell: p => <span className="font-medium">{p.name}</span>,
      },
      {
        id: "podcastFor",
        label: "Podcast For",
        width: 120,
        renderCell: p => p.podcast_for,
      },
      {
        id: "type",
        label: "Podcast Type",
        width: 120,
        renderCell: p => p.podcast_type,
      },
      {
        id: "link",
        label: "Link",
        width: 200,
        renderCell: p =>
          p.link ? (
            <a
              href={p.link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline"
            >
              {p.link}
            </a>
          ) : (
            "—"
          ),
      },
      {
        id: "enabled",
        label: "Enabled",
        width: 80,
        renderCell: p => (p.is_enabled ? "Yes" : "No"),
      },
      {
        id: "actions",
        label: "Action",
        width: 100,
        alwaysVisible: true,
        renderCell: podcast => (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => openEditModal(podcast)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDeleteId(podcast.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [currentPage, pageSize]
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Podcasts</h1>
          <p className="text-muted-foreground">
            Add and manage podcast entries for the public site
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
          <DialogTrigger asChild>
            <Button className="gap-2" onClick={openAddModal}>
              <Plus className="h-4 w-4" />
              Add Podcast
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {isEditMode ? "Edit Podcast" : "Add Podcast"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  placeholder="Education Excellence"
                  value={form.name}
                  onChange={e =>
                    setForm(prev => ({ ...prev, name: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Podcast For</Label>
                <Select
                  value={form.podcast_for}
                  onValueChange={value =>
                    setForm(prev => ({ ...prev, podcast_for: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select audience" />
                  </SelectTrigger>
                  <SelectContent>
                    {PODCAST_FOR_OPTIONS.map(opt => (
                      <SelectItem key={opt} value={opt}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Podcast Type</Label>
                <Select
                  value={form.podcast_type}
                  onValueChange={value =>
                    setForm(prev => ({ ...prev, podcast_type: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PODCAST_TYPE_OPTIONS.map(opt => (
                      <SelectItem key={opt} value={opt}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="link">Link</Label>
                <Input
                  id="link"
                  placeholder="https://youtube.com/watch?v=..."
                  value={form.link}
                  onChange={e =>
                    setForm(prev => ({ ...prev, link: e.target.value }))
                  }
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <Label htmlFor="is_enabled" className="cursor-pointer">
                  Enable podcast (visible to users)
                </Label>
                <Switch
                  id="is_enabled"
                  checked={form.is_enabled}
                  onCheckedChange={checked =>
                    setForm(prev => ({ ...prev, is_enabled: checked }))
                  }
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving && <CustomLoader size="xs" className="mr-2" />}
                  {isEditMode ? "Update" : "Save"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-3 pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <CustomLoader size="md" />
            </div>
          ) : (
            <DataTable
              title={`All Podcasts (${totalItems})`}
              tableId="super-admin-cms-podcasts"
              columns={tableColumns}
              data={podcasts}
              getRowKey={p => String(p.id)}
              emptyMessage='No podcasts yet. Click "Add Podcast" to create one.'
            />
          )}

          {!loading && totalItems > 0 && (
            <div className="mt-4 border-t pt-4">
              <CommonPagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalCount={totalItems}
                onPageChange={setCurrentPage}
                onPageSizeChange={size => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={deleteId !== null && deleteId !== undefined}
        onOpenChange={open => !open && setDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete podcast</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              podcast.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="text-destructive-foreground bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
